import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Tooltip,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  Zoom
} from '@mui/material';
import {
  Security as SecurityIcon,
  NetworkCheck as NetworkCheckIcon,
  Computer as ComputerIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import dashboardApiService from '../../services/dashboard/DashboardApiService';
import networkScanApiService from '../../services/network/NetworkScanApiService';

const NetworkDiscovery = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const [agentsLoading, setAgentsLoading] = useState(true);

  useEffect(() => {
    fetchAgents();
    
    // Auto-refresh agents every 30 seconds
    const interval = setInterval(() => {
      fetchAgents();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchAgents = async () => {
    try {
      setAgentsLoading(true);
      const agentsData = await dashboardApiService.getAllAgents();
      
      // Ensure all agent properties are properly formatted for React rendering
      const formattedAgents = agentsData.map(agent => ({
        ...agent,
        name: String(agent.name || 'Unknown Agent'),
        ip: String(agent.ip || 'Unknown IP'),
        status: String(agent.status || 'Unknown'),
        os: typeof agent.os === 'object' ? (agent.os.name || 'Unknown OS') : String(agent.os || 'Unknown OS'),
        // Normalize status for consistent color mapping
        normalizedStatus: agent.status === 'active' ? 'Active' : agent.status === 'disconnected' ? 'Disconnected' : String(agent.status || 'Unknown')
      }));
      
      setAgents(formattedAgents);
      
      // Auto-select first active agent, or first agent if none are active
      const activeAgents = formattedAgents.filter(agent => agent.normalizedStatus === 'Active');
      if (activeAgents.length > 0) {
        setSelectedAgent(activeAgents[0].id);
      } else if (formattedAgents.length > 0) {
        setSelectedAgent(formattedAgents[0].id);
      }
    } catch (err) {
      setError('Failed to fetch agents');
      console.error('Error fetching agents:', err);
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleScanPorts = async () => {
    if (!selectedAgent) return;

    const agent = agents.find(a => a.id === selectedAgent);
    if (!agent) return;

    // Check if agent is active before scanning
    if (agent.normalizedStatus !== 'Active') {
      setError(`Cannot scan ${agent.name}. Port scanning is only available for active agents. Current status: ${agent.normalizedStatus}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setScanResults(null);

      const results = await networkScanApiService.scanPorts(agent.id);
      setScanResults(results);
    } catch (err) {
      setError(`Failed to scan ports for ${agent.name}: ${err.message}`);
      console.error('Error scanning ports:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#fbc02d';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'critical': return <ErrorIcon sx={{ color: '#d32f2f' }} />;
      case 'high': return <WarningIcon sx={{ color: '#f57c00' }} />;
      case 'medium': return <InfoIcon sx={{ color: '#fbc02d' }} />;
      case 'low': return <CheckCircleIcon sx={{ color: '#388e3c' }} />;
      default: return <InfoIcon sx={{ color: '#757575' }} />;
    }
  };

  const getPortStatusIcon = (serviceName) => {
    const secureServices = ['https', 'ssh', 'smtps', 'imaps', 'pop3s'];
    const isSecure = secureServices.includes(serviceName.toLowerCase());
    
    return isSecure ? 
      <LockIcon sx={{ color: '#388e3c', fontSize: 16 }} /> : 
      <LockOpenIcon sx={{ color: '#f57c00', fontSize: 16 }} />;
  };

  const selectedAgentData = agents.find(a => a.id === selectedAgent);
  const categorizedPorts = scanResults ? networkScanApiService.categorizePorts(scanResults.open_ports) : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <NetworkCheckIcon sx={{ fontSize: 28, color: '#667eea' }} />
          Network Detection
        </Typography>
      </Box>

      {/* Agent Selection Card */}
      <Card sx={{ 
        mb: 3, 
        border: '1px solid #e0e0e0', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        }
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
          color: 'white',
          p: 2,
          borderRadius: '3px 3px 0 0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ComputerIcon sx={{ fontSize: 20 }} />
            Target Selection
          </Typography>
        </Box>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontWeight: 600 }}>Select Agent to Scan</InputLabel>
                  <Select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    label="Select Agent to Scan"
                    disabled={agentsLoading}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          mt: 1,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          borderRadius: 2,
                          '& .MuiMenuItem-root': {
                            minHeight: 'auto'
                          }
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                      '& .MuiSelect-select': {
                        py: 1.5
                      }
                    }}
                  >
                    {agents.map((agent) => (
                      <MenuItem 
                        key={agent.id} 
                        value={agent.id} 
                        disabled={agent.normalizedStatus !== 'Active'}
                        sx={{ 
                          py: 1,
                          minHeight: 48,
                          opacity: agent.normalizedStatus !== 'Active' ? 0.5 : 1,
                          cursor: agent.normalizedStatus !== 'Active' ? 'not-allowed' : 'pointer',
                          '&.Mui-disabled': {
                            opacity: 0.5,
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <Box sx={{
                            backgroundColor: agent.normalizedStatus === 'Active' ? '#e8f5e8' : '#ffebee',
                            borderRadius: '50%',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <ComputerIcon sx={{ 
                              color: agent.normalizedStatus === 'Active' ? '#4caf50' : '#f44336',
                              fontSize: 16 
                            }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 600, 
                              fontSize: '13px',
                              color: agent.normalizedStatus !== 'Active' ? '#999' : 'inherit',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {agent.name}
                              {agent.normalizedStatus !== 'Active' && (
                                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: '#f44336', fontWeight: 500, fontSize: '10px' }}>
                                  (Unavailable)
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: agent.normalizedStatus !== 'Active' ? '#bbb' : '#666', 
                              fontFamily: 'monospace',
                              fontSize: '11px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {agent.ip}
                            </Typography>
                          </Box>
                          <Chip
                            label={agent.normalizedStatus}
                            size="small"
                            sx={{ 
                              backgroundColor: agent.normalizedStatus === 'Active' ? '#4caf50' : agent.normalizedStatus === 'Disconnected' ? '#f44336' : '#9e9e9e',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '9px',
                              height: 20,
                              minWidth: 60,
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Button
                  variant="contained"
                  onClick={handleScanPorts}
                  disabled={!selectedAgent || loading}
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SearchIcon />}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 700,
                    px: 4,
                    py: 2.5,
                    borderRadius: 2,
                    fontSize: '16px',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#999',
                      border: '1px solid #d0d0d0'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {loading ? 'Scanning Network...' : 'Start Port Scan'}
                </Button>
              </Box>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 3,
            border: '1px solid #ffcdd2',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
            '& .MuiAlert-icon': {
              fontSize: 24
            },
            '& .MuiAlert-message': {
              fontWeight: 600,
              fontSize: '14px'
            }
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Loading Progress */}
      {loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Scanning Network Ports...</Typography>
            </Box>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
              Analyzing open ports and services on {selectedAgentData?.ip}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Scan Results */}
      {scanResults && (
        <Fade in={true} timeout={800}>
          <Box>
            {/* Target Information - Only shown after scan */}
            <Box sx={{ 
              mb: 4, 
              p: 4, 
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: 3,
              border: '2px solid #e3f2fd',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <InfoIcon sx={{ fontSize: 24, color: '#667eea' }} />
                Target Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'white', 
                    borderRadius: 3, 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #f0f0f0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: '#666', 
                      fontWeight: 700, 
                      textTransform: 'uppercase', 
                      letterSpacing: '1px',
                      mb: 1.5
                    }}>
                      Agent ID
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                      {selectedAgentData?.id}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'white', 
                    borderRadius: 3, 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #f0f0f0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: '#666', 
                      fontWeight: 700, 
                      textTransform: 'uppercase', 
                      letterSpacing: '1px',
                      mb: 1.5
                    }}>
                      IP Address
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      color: '#2c3e50', 
                      fontFamily: 'monospace',
                      backgroundColor: '#f5f5f5',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1
                    }}>
                      {scanResults.target}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'white', 
                    borderRadius: 3, 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #f0f0f0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: '#666', 
                      fontWeight: 700, 
                      textTransform: 'uppercase', 
                      letterSpacing: '1px',
                      mb: 1.5
                    }}>
                      Operating System
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                      {selectedAgentData?.os}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'white', 
                    borderRadius: 3, 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #f0f0f0',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: '#666', 
                      fontWeight: 700, 
                      textTransform: 'uppercase', 
                      letterSpacing: '1px',
                      mb: 1.5
                    }}>
                      Status
                    </Typography>
                    <Chip
                      label={selectedAgentData?.normalizedStatus}
                      sx={{
                        backgroundColor: selectedAgentData?.normalizedStatus === 'Active' ? '#4caf50' : selectedAgentData?.normalizedStatus === 'Disconnected' ? '#f44336' : '#9e9e9e',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '12px',
                        height: 32,
                        px: 2,
                        boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                        '& .MuiChip-label': {
                          px: 2
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ShieldIcon sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {scanResults.total_open_ports || 0}
                        </Typography>
                        <Typography variant="body2">Total Ports</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {scanResults.truly_open_ports || 0}
                        </Typography>
                        <Typography variant="body2">Listening Ports</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)', color: 'white' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VisibilityOffIcon sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {scanResults.filtered_ports || 0}
                        </Typography>
                        <Typography variant="body2">Filtered Ports</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* All Ports Table */}
            <Card sx={{ mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 2
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SecurityIcon sx={{ fontSize: 24 }} />
                  All Detected Ports ({scanResults.total_open_ports || 0})
                </Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Port</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Protocol</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Process</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Local IP</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Remote IP</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Risk</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanResults.open_ports && scanResults.open_ports.length > 0 ? (
                        scanResults.open_ports.map((port, index) => {
                          const risk = networkScanApiService.getPortRisk(port.port);
                          return (
                            <TableRow 
                              key={index} 
                              hover
                              sx={{
                                '&:hover': { backgroundColor: '#f8f9fa' },
                                backgroundColor: port.state === 'filtered' ? '#fff3e0' : 'white'
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '14px' }}>
                                  {port.port}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={port.protocol.toUpperCase()} 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: port.protocol === 'tcp' ? '#e3f2fd' : '#f3e5f5',
                                    color: port.protocol === 'tcp' ? '#1976d2' : '#7b1fa2',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={port.state.toUpperCase()}
                                  size="small"
                                  icon={port.state === 'listening' ? <CheckCircleIcon /> : <VisibilityOffIcon />}
                                  sx={{
                                    backgroundColor: port.state === 'listening' ? '#4caf50' : port.state === 'established' ? '#2196f3' : '#9e9e9e',
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: 'white' }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {port.process ? (
                                  <Chip 
                                    label={port.process} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ 
                                      fontWeight: 600,
                                      fontFamily: 'monospace',
                                      backgroundColor: '#e3f2fd',
                                      borderColor: '#2196f3',
                                      color: '#1976d2'
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#2c3e50' }}>
                                  {port.local_ip || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#666' }}>
                                  {port.remote_ip && port.remote_ip !== '0.0.0.0' ? port.remote_ip : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={risk.toUpperCase()}
                                  size="small"
                                  sx={{
                                    backgroundColor: getRiskColor(risk),
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              No ports detected
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      )}

      {/* Empty State */}
      {!loading && !scanResults && !error && (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Zoom in={true} timeout={500}>
              <SecurityIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
            </Zoom>
            <Typography variant="h6" sx={{ mb: 1, color: '#666' }}>
              Ready to Scan Network Ports
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              Select an agent and click "Start Port Scan" to analyze open ports and security risks
            </Typography>
            <Button
              variant="contained"
              onClick={handleScanPorts}
              disabled={!selectedAgent}
              startIcon={<SearchIcon />}
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                fontWeight: 600
              }}
            >
              Start Your First Scan
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default NetworkDiscovery;
